// utility functions
import { env } from '@/app/config/environment';

/**
 * Generates a signed URL for Google Cloud Storage media
 * @param mediaId - The media ID from the business API response (can be null)
 * @param type - The type of media ('image' or 'video')
 * @returns Object containing the URL and media type for developer clarity. Returns empty URL if mediaId is null.
 */
export const getSignedMediaUrl = (mediaId: string | null, type: 'image' | 'video' = 'image') => {
  if (!mediaId) {
    return {
      url: '',
      type,
      isImage: () => type === 'image',
      isVideo: () => type === 'video'
    };
  }

  const baseUrl = env.MAIN_GCS_URL;
  const url = `${baseUrl}/${mediaId}`;

  return {
    url,
    type,
    // Helper methods for developer convenience
    /*
    @returns True if the media is an image, false otherwise
    @returns True if the media is a video, false otherwise
    example usage:
      const media = getSignedMediaUrl(mediaId, 'image');
      if (media.isImage()) {
        console.log('The media is an image');
      } else {
        console.log('The media is a video');
      }
    
    */
    isImage: () => type === 'image',
    isVideo: () => type === 'video'
  };
};

/**
 * Gets the best available image URL for a business or event
 * @param cardImageId - The card_image_id from the API response
 * @param media - The media array from the API response
 * @param fallback - Fallback image path if no image is available
 * @returns The best available image URL
 */
export const getBestImageUrl = (
  cardImageId: string | null | undefined,
  media: Array<{ media_id: string; rank?: number }> | null | undefined,
  fallback: string = '/placeholder.jpg'
): string => {
  // Check if card_image_id is valid (not null, undefined, or the default empty UUID)
  if (cardImageId && cardImageId !== '00000000-0000-0000-0000-000000000000') {
    return getSignedMediaUrl(cardImageId, 'image').url;
  }

  // Fallback to first media item if available
  if (media && media.length > 0) {
    return getSignedMediaUrl(media[0].media_id, 'image').url;
  }

  // Return fallback image
  return fallback;
};

/**
 * Gets the best available video URL for a business or event
 * @param cardVideoId - The card_video_id from the API response
 * @returns The best available video URL or null if no video is available
 */
export const getBestVideoUrl = (
  cardVideoId: string | null | undefined
): string | null => {
  // Check if card_video_id is valid (not null, undefined, or the default empty UUID)
  if (cardVideoId && cardVideoId !== '00000000-0000-0000-0000-000000000000') {
    return getSignedMediaUrl(cardVideoId, 'video').url;
  }

  // Return null if no video is available
  return null;
};


/**
 * Detects media type based on file extension in signed URL or fallback patterns
 * @param signedUrlId - The signed URL ID from the API response
 * @returns 'image' or 'video' based on file extension or patterns
 */
export const detectMediaType = (signedUrlId: string): 'image' | 'video' => {
  // Video file extensions
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.flv', '.wmv'];

  // Image file extensions
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];

  // Convert to lowercase for case-insensitive comparison
  const lowerId = signedUrlId.toLowerCase();

  // Check for video extensions first
  for (const ext of videoExtensions) {
    if (lowerId.includes(ext)) {
      return 'video';
    }
  }

  // Check for image extensions
  for (const ext of imageExtensions) {
    if (lowerId.includes(ext)) {
      return 'image';
    }
  }

  // Fallback: Check for video patterns in UUID (common in this codebase)
  if (lowerId.includes('video') || lowerId.includes('mp4')) {
    return 'video';
  }

  // Default to image if no pattern matches (most media is images)
  return 'image';
};

/**
 * Formats a date string to a readable format
 * @param dateString - ISO date string
 * @param timezone - Optional timezone identifier (e.g., 'Europe/London')
 * @returns Formatted date string
 */
export const formatDate = (dateString: string, timezone?: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };

  if (timezone) {
    options.timeZone = timezone;
  }

  return new Date(dateString).toLocaleDateString('en-US', options);
};

/**
 * Formats time duration from nanoseconds to human readable format
 * @param duration - Duration in nanoseconds
 * @returns Formatted duration string
 */
export const formatDuration = (duration: number): string => {
  const hours = Math.floor(duration / (1000 * 1000 * 1000 * 60 * 60));
  const minutes = Math.floor((duration % (1000 * 1000 * 1000 * 60 * 60)) / (1000 * 1000 * 1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
  }
  return `${minutes}m`;
};

/**
 * Gets day of week name from number
 * @param dayOfWeek - Day number (0-6, where 0 is Sunday)
 * @returns Day name
 */
export const getDayName = (dayOfWeek: number): string => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[dayOfWeek] || 'Unknown';
};

/**
 * Formats a date string to a detailed readable format with weekday
 * @param dateString - ISO date string
 * @param timezone - Optional timezone identifier (e.g., 'Europe/London')
 * @returns Formatted date string with weekday, year, month, and day
 */
export const formatDetailedDate = (dateString: string, timezone?: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };

  if (timezone) {
    options.timeZone = timezone;
  }

  return new Date(dateString).toLocaleDateString('en-UK', options);
};

export const formatTime = (dateString: string, timezone?: string, includeTimezone: boolean = false): string => {
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };

  if (timezone) {
    options.timeZone = timezone;
    if (includeTimezone) {
      options.timeZoneName = 'short';
    }
  }

  return new Date(dateString).toLocaleTimeString('en-UK', options);
};

/**
 * Formats a date string to time format, appending +1 if it occurs on a day after the base date in the given timezone.
 */
export const formatTimeWithNextDay = (dateString: string, baseDateString: string, timezone?: string, includeTimezone: boolean = false): string => {
  const timeStr = formatTime(dateString, timezone, includeTimezone);
  if (!baseDateString) return timeStr;

  try {
    const baseDateOpts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
    if (timezone) baseDateOpts.timeZone = timezone;
    const fmtBase = new Intl.DateTimeFormat('en-US', baseDateOpts).format(new Date(baseDateString));
    const fmtSlot = new Intl.DateTimeFormat('en-US', baseDateOpts).format(new Date(dateString));

    if (fmtBase !== fmtSlot) {
      // It's on a different day (assuming next day)
      return `${timeStr}+1`;
    }
  } catch (e) {
    console.error('Error formatting next day time', e);
  }

  return timeStr;
};


/**
 * Formats an ISO timestamp to a readable time format (e.g., "3:45 PM")
 * @param isoString - ISO date string
 * @returns Formatted time string in 12-hour format (e.g., "3:45 PM")
 */
export const formatTimeReadable = (isoString: string): string => {
  const date = new Date(isoString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid time';
  }

  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };

  return date.toLocaleTimeString('en-US', options);
};

/**
 * Formats an ISO timestamp to a short readable date format (e.g., "Dec 29, 2025")
 * @param isoString - ISO date string (can be undefined/null)
 * @param fallback - Fallback string if date is invalid (default: 'N/A')
 * @returns Formatted date string in short format
 */
export const formatShortDate = (isoString?: string | null, fallback: string = 'N/A'): string => {
  if (!isoString) return fallback;

  const date = new Date(isoString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return fallback;
  }

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };

  return date.toLocaleDateString('en-US', options);
};

/**
 * Formats an ISO timestamp to a short readable date and time format (e.g., "Dec 29, 9:52 PM")
 * @param isoString - ISO date string (can be undefined/null)
 * @param fallback - Fallback string if date is invalid (default: 'N/A')
 * @returns Formatted date and time string
 */
export const formatShortDateTime = (isoString?: string | null, fallback: string = 'N/A'): string => {
  if (!isoString) return fallback;

  const date = new Date(isoString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return fallback;
  }

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };

  return date.toLocaleString('en-US', options);
};

/**
 * Formats slot duration from nanoseconds to human readable format
 * @param nanoseconds - Duration in nanoseconds
 * @returns Formatted duration string (e.g., "2h 30m" or "1h")
 */
export const formatSlotDuration = (nanoseconds: number): string => {
  const totalSeconds = nanoseconds / 1e9;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
};

/**
 * Formats a UTC timestamp to business local time with timezone name
 * @param utcTime - UTC timestamp string (e.g., "2025-10-10T18:00:00Z")
 * @param businessTimeZone - IANA timezone identifier (e.g., "Europe/London")
 * @param options - Optional formatting options
 * @returns Formatted time string in business timezone
 */
export const formatBusinessTime = (
  utcTime: string,
  businessTimeZone: string,
  options: {
    includeTimezone?: boolean;
    format?: 'time' | 'datetime' | 'date';
    locale?: string;
  } = {}
): string => {
  const {
    includeTimezone = true,
    format = 'time',
    locale = 'en-GB'
  } = options;

  const formatOptions: Intl.DateTimeFormatOptions = {
    timeZone: businessTimeZone,
  };

  if (includeTimezone) {
    formatOptions.timeZoneName = 'short';
  }

  switch (format) {
    case 'time':
      formatOptions.hour = '2-digit';
      formatOptions.minute = '2-digit';
      formatOptions.hour12 = true;
      break;
    case 'datetime':
      formatOptions.weekday = 'short';
      formatOptions.month = 'short';
      formatOptions.day = 'numeric';
      formatOptions.hour = '2-digit';
      formatOptions.minute = '2-digit';
      formatOptions.hour12 = true;
      break;
    case 'date':
      formatOptions.weekday = 'long';
      formatOptions.year = 'numeric';
      formatOptions.month = 'long';
      formatOptions.day = 'numeric';
      break;
  }

  return new Date(utcTime).toLocaleString(locale, formatOptions);
};

/**
 * Converts UTC time to business local time and returns a Date object
 * @param utcTime - UTC timestamp string
 * @param businessTimeZone - IANA timezone identifier
 * @returns Date object representing the time in business timezone
 */
export const convertToBusinessTime = (utcTime: string, businessTimeZone: string): Date => {
  // Create a date object from the UTC time
  const utcDate = new Date(utcTime);

  // Get the timezone offset for the business timezone
  const businessTime = new Date(utcDate.toLocaleString('en-US', { timeZone: businessTimeZone }));
  const utcTime2 = new Date(utcDate.toLocaleString('en-US', { timeZone: 'UTC' }));

  // Calculate the offset
  const offset = businessTime.getTime() - utcTime2.getTime();

  // Apply the offset to get the business local time
  return new Date(utcDate.getTime() + offset);
};

// Export form validation utilities
export * from './formValidation';

// Export error handling utilities
export * from './errorHandling';

// Export date string utilities
export * from './dateStringUtils';

// Export phone utilities
export * from './phoneUtils';

// Export currency utilities

export * from './currencyFormat';
export * from './clubHandleHelper';

// Export className utility
export * from './cn';

/**
 * Converts an ISO 8601 UTC date string to a human-readable format
 * for a specified IANA timezone.
 *
 * @param isoString The date string in ISO 8601 format (e.g., "2025-10-11T11:00:00Z").
 * @param timeZone The IANA timezone ID (e.g., "Europe/London", "America/New_York").
 * @returns A formatted, human-readable date and time string, or an error message.
 */
export function formatDateTimeByTimezone(isoString: string, timeZone: string): string {
  try {
    const date = new Date(isoString);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return "Invalid date provided.";
    }

    // Use Intl.DateTimeFormat for robust timezone handling and locale-aware formatting.
    // The 'timeZoneName: short' option is what includes abbreviations like 'BST'.
    const formatter = new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      timeZone: timeZone,
      hour12: true, // Use 12-hour format with AM/PM
    });

    return formatter.format(date);
  } catch (error) {
    // Intl.DateTimeFormat will throw a RangeError for invalid timezones.
    if (error instanceof RangeError) {
      return `Invalid timezone provided: ${timeZone}`;
    }
    // Handle other potential errors
    return `An unexpected error occurred: ${error instanceof Error ? error.message : String(error)}`;
  }
}

/**
 * Extracts the timezone abbreviation (e.g., BST, EDT) for a given date and timezone.
 *
 * @param isoString The date string in ISO 8601 format.
 * @param timeZone The IANA timezone ID.
 * @returns The timezone abbreviation string, or an error message.
 */
export function getTimezoneAbbreviation(isoString: string, timeZone: string): string {
  try {
    const date = new Date(isoString);

    if (isNaN(date.getTime())) {
      return "Invalid date provided.";
    }

    // Formatter specifically to extract the timezone name.
    const formatter = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'short',
      timeZone: timeZone,
    });

    // formatToParts returns an array of objects representing the formatted date.
    // We can find the part that corresponds to the timezone name.
    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find((part) => part.type === 'timeZoneName');

    return timeZonePart ? timeZonePart.value : 'Abbreviation not available';
  } catch (error) {
    if (error instanceof RangeError) {
      return `Invalid timezone provided: ${timeZone}`;
    }
    return `An unexpected error occurred: ${error instanceof Error ? error.message : String(error)}`;
  }
}

/**
 * Formats a date string for display in a specific timezone with various format options
 * @param isoString The date string in ISO 8601 format
 * @param timeZone The IANA timezone ID
 * @param options Formatting options
 * @returns Formatted date string
 */
export function formatDateByTimezone(
  isoString: string,
  timeZone: string,
  options: {
    format?: 'date' | 'time' | 'datetime' | 'weekday' | 'month-day' | 'full';
    includeTimezone?: boolean;
    locale?: string;
  } = {}
): string {
  try {
    const date = new Date(isoString);

    if (isNaN(date.getTime())) {
      return "Invalid date provided.";
    }

    const {
      format = 'datetime',
      includeTimezone = true,
      locale = 'en-US'
    } = options;

    const formatOptions: Intl.DateTimeFormatOptions = {
      timeZone: timeZone,
    };

    if (includeTimezone) {
      formatOptions.timeZoneName = 'short';
    }

    switch (format) {
      case 'date':
        formatOptions.year = 'numeric';
        formatOptions.month = 'long';
        formatOptions.day = 'numeric';
        break;
      case 'time':
        formatOptions.hour = 'numeric';
        formatOptions.minute = '2-digit';
        formatOptions.hour12 = true;
        break;
      case 'datetime':
        formatOptions.year = 'numeric';
        formatOptions.month = 'long';
        formatOptions.day = 'numeric';
        formatOptions.hour = 'numeric';
        formatOptions.minute = '2-digit';
        formatOptions.hour12 = true;
        break;
      case 'weekday':
        formatOptions.weekday = 'long';
        break;
      case 'month-day':
        formatOptions.month = 'short';
        formatOptions.day = '2-digit';
        break;
      case 'full':
        formatOptions.weekday = 'long';
        formatOptions.year = 'numeric';
        formatOptions.month = 'long';
        formatOptions.day = 'numeric';
        formatOptions.hour = 'numeric';
        formatOptions.minute = '2-digit';
        formatOptions.hour12 = true;
        break;
    }

    return new Intl.DateTimeFormat(locale, formatOptions).format(date);
  } catch (error) {
    if (error instanceof RangeError) {
      return `Invalid timezone provided: ${timeZone}`;
    }
    return `An unexpected error occurred: ${error instanceof Error ? error.message : String(error)}`;
  }
}
